import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The three cameras that matter most for a daily Paisley commuter:
// the Erskine Bridge crossing, Glasgow Airport, and the M77 south of Glasgow.
const FEATURED = ['Erskine Br Gantry', 'Glasgow Airport', 'Dumbreck Rd (M77)'];

const LiveCameraStrip = () => {
  const [cams, setCams] = useState([]);
  const [weather, setWeather] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [camsR, wxR] = await Promise.all([
          fetch(`${API}/traffic-cameras`, { cache: 'no-store' }),
          fetch(`${API}/weather`,         { cache: 'no-store' }),
        ]);
        const camsD = await camsR.json();
        const wxD   = await wxR.json().catch(() => null);
        if (!alive) return;
        const picked = FEATURED
          .map((name) => (camsD.cameras || []).find((c) => c.name === name))
          .filter(Boolean);
        setCams(picked);
        if (wxD) setWeather(wxD);
      } catch { /* empty */ }
    };
    load();
    const id = setInterval(() => { load(); setTick((t) => t + 1); }, 7 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (cams.length === 0) return null;

  return (
    <section className="bg-slate-50 border-y border-slate-200" data-testid="home-live-camera-strip">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-cyan-700 mb-1 inline-flex items-center gap-1.5">
              <Camera size={11} /> Roads right now
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900" data-testid="home-cameras-h2">
              Live from the Renfrewshire roads
            </h2>
          </div>
          <Link
            to="/roads-and-travel"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-cyan-700 transition-colors"
            data-testid="home-cameras-view-all"
          >
            View all 75 cameras <ArrowUpRight size={13} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {cams.map((c) => (
            <Link
              key={c.id}
              to="/roads-and-travel"
              className="group relative block rounded-xl overflow-hidden ring-1 ring-slate-200 hover:ring-cyan-400 hover:shadow-lg transition-all aspect-[16/10] bg-slate-200"
              data-testid={`home-cam-${c.image_name.replace('.jpg', '')}`}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}?t=${tick}`}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
              />
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-rose-500 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
              {weather && (() => {
                const hhmm = (iso) => iso ? iso.slice(11, 16) : '';
                const now = new Date();
                const sunset = weather.sunset ? new Date(weather.sunset) : null;
                const isDay = !sunset || now < sunset;
                const next = isDay
                  ? { label: 'Sunset',  icon: '↓', time: hhmm(weather.sunset)  }
                  : { label: 'Sunrise', icon: '↑', time: hhmm(weather.sunrise) };
                return (
                  <span
                    className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-black/65 backdrop-blur-sm text-white text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-white/10"
                    title={`${weather.label} · ${next.label} ${next.time}`}
                    data-testid="home-cam-weather-pill"
                  >
                    <span className="text-sm leading-none">{weather.icon}</span>
                    {Math.round(weather.temp_c)}°
                    {next.time && (
                      <>
                        <span className="text-white/30">·</span>
                        <span className="text-amber-300" data-testid="home-cam-sun-chip">{next.icon} {next.time}</span>
                      </>
                    )}
                  </span>
                );
              })()}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent p-3">
                <p className="text-white font-semibold text-sm">{c.name}</p>
              </div>
            </Link>
          ))}
        </div>

        <p className="text-[10px] text-slate-400 mt-3">
          Images supplied by{' '}
          <a href="https://www.traffic.gov.scot/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
            Traffic Scotland
          </a>.
        </p>
      </div>
    </section>
  );
};

export default LiveCameraStrip;
